import React, { useState } from 'react';
import { Container, Typography, CircularProgress, Alert } from '@mui/material';
import { useDashboard } from '../hooks/useDashboard';
import FinancialFilter from '../components/financialAnalytics/FinancialFilter';
import FinancialRevenueComparison from '../components/financialAnalytics/FinancialRevenueComparison';
import { useWarehouseContext } from '../context/WarehouseContext';
import { useTranslation } from 'react-i18next';
import { toISTDateString } from '../utils/dates';
import analyticsCache from '../utils/analyticsCache';

const FinancialAnalytics = () => {
    const { t } = useTranslation();
    const { getFinancialGstSummary, getFinancialTcs } = useDashboard();

    const [period, setPeriod] = useState('last_30_days');
    const [dateRange, setDateRange] = useState(() => {
        const today = new Date();
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return {
            from_date: toISTDateString(startOfMonth),
            to_date: toISTDateString(endOfMonth),
        };
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { selectedWarehouseIds } = useWarehouseContext();

    const [data, setData] = useState({ gst: null, tcs: null });

    const fetchingRef = React.useRef(false);

    const fetchData = React.useCallback(async (force = false) => {
        if (period === 'custom' && (!dateRange.from_date || !dateRange.to_date)) return;
        if (fetchingRef.current) return;

        const warehouseKey = selectedWarehouseIds?.length > 0 ? `_w${selectedWarehouseIds.join('_')}` : '';
        const cacheKey = period === 'custom'
            ? `financialAnalytics_custom_${dateRange.from_date}_${dateRange.to_date}${warehouseKey}`
            : `financialAnalytics_${period}${warehouseKey}`;
        const cachedData = analyticsCache.getItem(cacheKey);

        if (!force && cachedData) {
            try {
                const parsed = JSON.parse(cachedData);
                if (parsed) { setData(parsed); return; }
            } catch (e) {
                analyticsCache.removeItem(cacheKey);
            }
        }

        fetchingRef.current = true;
        setLoading(true);
        setError(null);

        try {
            const params = { period };
            if (period === 'custom') {
                params.from_date = dateRange.from_date;
                params.to_date = dateRange.to_date;
            }

            const [gstRes, tcsRes] = await Promise.all([
                getFinancialGstSummary(params),
                getFinancialTcs(params)
            ]);

            const newError = gstRes.error || tcsRes.error;
            if (newError) setError(newError);

            const newData = {
                gst: gstRes.data || null,
                tcs: tcsRes.data || null
            };

            setData(newData);
            analyticsCache.setItem(cacheKey, JSON.stringify(newData));
        } catch (err) {
            setError(err.message || t('hookErrors.analyticsPage.fetchFailed'));
        } finally {
            setLoading(false);
            fetchingRef.current = false;
        }
    }, [period, dateRange, getFinancialGstSummary, getFinancialTcs, selectedWarehouseIds]);

    React.useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedWarehouseIds]);

    const strings = t('financialAnalytics.page', { returnObjects: true });

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Header */}
            <div style={{ marginBottom: 36 }}>
                <Typography
                    variant="h3"
                    component="h1"
                    fontWeight="800"
                    sx={{
                        background: 'linear-gradient(45deg, #05445E 30%, #189AB4 90%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 1
                    }}
                >
                    {strings.title}
                </Typography>
                <Typography variant="subtitle1" color="textSecondary" sx={{ fontWeight: 500 }}>
                    {strings.subtitle}
                </Typography>
            </div>

            <FinancialFilter
                fromDate={dateRange.from_date}
                toDate={dateRange.to_date}
                onFromDateChange={(val) => setDateRange(prev => ({ ...prev, from_date: val }))}
                onToDateChange={(val) => setDateRange(prev => ({ ...prev, to_date: val }))}
                onSubmit={() => fetchData(true)}
                loading={loading}
                period={period}
                onPeriodChange={setPeriod}
            />

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {typeof error === 'object' ? JSON.stringify(error) : error}
                </Alert>
            )}

            {loading && !data.gst && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                    <CircularProgress />
                </div>
            )}

            {data.gst && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                    <FinancialRevenueComparison />
                </div>
            )}
        </Container>
    );
};

export default FinancialAnalytics;
